import React, { FC, useEffect, useState } from "react";

// MUI Components
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Tooltip from "@mui/material/Tooltip";

// Icons
import { Cancel } from "@mui/icons-material";

// App Imports
import { generateUniqueKey } from "src/utils";
import GlobalStateActions from "src/redux/slices/GlobalStateActions";
import { replaceUrlParam } from "src/services/Utility";
import { useAppDispatch, useAppSelector } from "src/hooks";
import {
    User,
    TabProperties,
    Company,
    Product,
    FolderNode,
    ReturnNode,
    FixMeLater,
} from "src/types";

// App Components
import CustomSnackbar from "../CustomSnackbar/CustomSnackbar";
import DataTable from "../DataTable/DataTable";
import PdfViewer from "../PdfViewer/PdfViewer";
import { getReturnNodeIcon } from "../ReturnNode/ReturnNodeUtil";
import TabPanel from "./TabPanel/TabPanel";

// App Styles
import "./MultiTab.scss";
import { UserService } from "src/services";
import { IconButton, Menu, MenuItem } from "@mui/material";

const appBarStyles = {
    appBarContainer: {
        flexDirection: "row",
        bgcolor: "#354560",
    },
};

interface MultiTabProps {
    splitScreen?: boolean;
    setSplitScreen?: (value: boolean) => void;
    addTab?: boolean;
    tabs?: Map<
        string,
        {
            key: string;
            tabName: string;
            taxYear: string;
            companyName: string;
            tooltip: string;
            scheduleType;
            isLocked;
            component: React.ReactElement;
        }
    >;
    setTabs?: React.Dispatch<
        React.SetStateAction<
            Map<
                string,
                {
                    key: string;
                    tabName: string;
                    taxYear: string;
                    companyName: string;
                    tooltip: string;
                    scheduleType;
                    isLocked;
                    component: React.ReactElement;
                }
            >
        >
    >;
    addTabToSplitScreen?: (value: string) => void;
    setActiveReturn?: (value: string) => void;
}

const MultiTab: FC<MultiTabProps> = ({
    splitScreen = false,
    setSplitScreen = () => {},
    addTab = true,
    tabs = new Map(),
    setTabs = () => {},
    addTabToSplitScreen = () => {},
    setActiveReturn = () => {},
}) => {
    const dispatch = useAppDispatch();

    const userService = UserService.getInstance();

    const product: Product | undefined = useAppSelector(
        (state) => state?.Product?.value,
    );

    if (!product) return null;

    const scheduleType = useAppSelector(
        (state) =>
            state?.[product?.productName]?.value?.returnNode?.scheduleType,
    );
    const company: Company = useAppSelector(
        (state) => state?.[product?.productName]?.value?.company,
    );

    const folderNode: FolderNode = useAppSelector(
        (state) => state?.[product?.productName]?.value?.folderNode,
    );

    const returnNode: ReturnNode = useAppSelector(
        (state) => state?.[product?.productName]?.value?.returnNode,
    );

    const [key, setKey] = useState<string>();

    const self: User | undefined = useAppSelector(
        (state) => state?.Self,
    )?.value;

    const tabsProperties = useAppSelector(
        (state) => state?.Tabs,
    )?.tabsProperties;

    const municipalState: string = useAppSelector(
        (state) => state?.Municipal?.value.selectedState,
    );
    const selectedQuarter = useAppSelector(
        (state: FixMeLater) =>
            state?.[product?.productName]?.value?.selectedQuarter,
    );

    const userOptions = useAppSelector((state) => state?.UserOptions?.value);

    const [contextMenu, setContextMenu] = useState<{
        mouseX: number;
        mouseY: number;
        tabKey: string;
    } | null>(null);

    const [splitScreenTabKey, setSplitScreenTabKey] = useState<string>("");

    const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
    const [snackbarMessage, setSnackbarMessage] = useState<string>("");
    const [snackbarStatus, setSnackbarStatus] = useState<string>("success");
    const MAX_AMOUNT_TABS = 20;

    const clearTabs = () => {
        dispatch(GlobalStateActions.setSelf({ ...self!!, pendingChanges: [] }));
        dispatch(GlobalStateActions[product?.productName].setReturnNode(null));
        dispatch(GlobalStateActions[product?.productName].setFolderNode(null));
        dispatch(GlobalStateActions.setTabs({}));
        replaceUrlParam("folderNodeId", null);
        replaceUrlParam("returnNodeId", null);
        replaceUrlParam("year", null);
        replaceUrlParam("companyId", null);
        setTabs(new Map());
    };

    const generateTab = (
        company: Company,
        product: Product,
        folderNode: FolderNode,
        returnNode: ReturnNode,
    ) => {
        const newKey = generateUniqueKey(
            folderNode,
            returnNode,
            product,
            company,
            municipalState,
            selectedQuarter,
        );
        let tooltip = `${folderNode.attributes.displayName}-${product?.taxYear} - ${company?.name} : ${returnNode?.displayName}`;
        if (product.productId == 2) {
            tooltip = `${product?.taxYear} - ${company?.name} - ${municipalState} - ${selectedQuarter} : ${returnNode?.displayName}`;
        }
        const tabName = `${product?.taxYear?.toString().slice(-2)} ${
            returnNode?.displayName
        }`;
        const taxYear = product?.taxYear.toString();
        const companyName = company?.name;

        if (!tabs.has(newKey)) {
            if (tabs.size >= MAX_AMOUNT_TABS) {
                setSnackbarOpen(true);
                setSnackbarMessage(
                    "You have reached the maximum amount of tabs. Please close some tabs to open a new one.",
                );
                setSnackbarStatus("error");
            } else {
                const newItem = {
                    key: newKey,
                    tabName,
                    taxYear,
                    companyName,
                    tooltip,
                    scheduleType: returnNode?.scheduleType,
                    isLocked: returnNode?.isLocked,
                    returnId: returnNode?.id,
                    component:
                        scheduleType === "RETURN" ? (
                            <PdfViewer
                                documentKey={newKey}
                                isActive={false}
                                key={newKey}
                                company={{ ...company }}
                                product={{ ...product }}
                                folderNode={{ ...folderNode }}
                                returnNode={{ ...returnNode }}
                            />
                        ) : (
                            <DataTable
                                documentKey={newKey}
                                isActive={false}
                                key={newKey}
                                company={{ ...company }}
                                product={{ ...product }}
                                folderNode={{ ...folderNode }}
                                returnNode={{ ...returnNode }}
                            />
                        ),
                };
                const updatedTabs = new Map(tabs);
                updatedTabs.set(newKey, newItem);
                setTabs(updatedTabs);
                setValue(updatedTabs.size - 1);
                const tabProperties: TabProperties = {
                    id: newKey,
                    isLocked: returnNode?.isLocked,
                    returnStatus: 0,
                };
                dispatch(GlobalStateActions.setTab(tabProperties));
            }
        }
        setKey(newKey);
    };

    useEffect(() => {
        if (company && product && folderNode && returnNode && addTab) {
            generateTab(company, product, folderNode, returnNode);
        }
    }, [folderNode, returnNode]);

    useEffect(() => {
        return () => {
            clearTabs();
        };
    }, []);

    useEffect(() => {
        if (userOptions && company && product) {
            const saveUserOptions = async () => {
                try {
                    await userService.setUserOptions(userOptions);
                } catch (error) {
                    console.error("Error saving user options", error);
                }
            };
            saveUserOptions();
        }
    }, [userOptions]);

    const [value, setValue] = useState(0);

    const handleChange = (event: React.SyntheticEvent, newValue: number) => {
        setValue(newValue);
        setKey(Array.from(tabs.keys())[newValue]);
    };

    useEffect(() => {
        const selectedIndex = Array.from(tabs.keys()).findIndex(
            (item) => item === key,
        );
        if (selectedIndex >= 0) {
            setValue(selectedIndex);
        }

        Array.from(tabs.values()).map((item, index) => {
            if (index === value && item) {
                setActiveReturn(item?.returnId);
            }
        });
    }, [key]);

    const pendingChangesOnTab = (key: string) => {
        return self?.pendingChanges?.find((tab) => tab === key);
    };

    const handleDeleteClick = (
        event: React.MouseEvent<HTMLDivElement, MouseEvent>,
        index: number,
        key: string,
    ) => {
        if (
            pendingChangesOnTab(key) &&
            !window.confirm(
                "You have unsaved changes. Do you really want to leave?",
            )
        ) {
            return;
        } else {
            dispatch(GlobalStateActions.removePendingChange(key));
            dispatch(GlobalStateActions.removeTab(key));
            dispatch(
                GlobalStateActions[product?.productName].setReturnNode(null),
            );
            dispatch(
                GlobalStateActions[product?.productName].setFolderNode(null),
            );
            replaceUrlParam("folderNodeId", null);
            replaceUrlParam("returnNodeId", null);
            replaceUrlParam("year", null);
            replaceUrlParam("companyId", null);
            const updatedTabs = new Map(tabs);
            updatedTabs.delete(key);
            const tabsArray = Array.from(updatedTabs.keys());
            setTabs(updatedTabs);
            let newValue =
                value >= updatedTabs.size
                    ? updatedTabs.size - 1
                    : value > index
                      ? value - 1
                      : value;
            setKey(tabsArray[newValue]);
            setValue(newValue);
        }
        event.stopPropagation();
    };

    const handleCloseAllClick = () => {
        if (
            self?.pendingChanges?.length &&
            !window.confirm(
                "You have unsaved changes. Do you really want to leave?",
            )
        ) {
            return;
        } else {
            clearTabs();
        }
    };

    const determineTabTextColor = (currentYear, tabYear, isSameCompany) => {
        if (tabYear == currentYear) {
            return isSameCompany ? "white" : "yellow";
        } else {
            return isSameCompany ? "yellow" : "orange";
        }
    };

    const handleRightClick = (
        event: React.MouseEvent<HTMLDivElement, MouseEvent>,
        mapKey: string,
    ) => {
        event.preventDefault();
        setContextMenu(
            contextMenu === null
                ? {
                      mouseX: event.clientX - 2,
                      mouseY: event.clientY - 4,
                      tabKey: mapKey,
                  }
                : null,
        );
        setSplitScreenTabKey(mapKey);
    };

    const handleMenuClose = () => {
        setContextMenu(null);
    };

    const handleSplitScreen = () => {
        addTabToSplitScreen(splitScreenTabKey);
        setSplitScreen(true);
        handleMenuClose();
    };

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                height: "100%",
                maxHeight: "100%",
            }}
        >
            <AppBar
                position="static"
                className="app-bar-container-class"
                sx={appBarStyles.appBarContainer}
            >
                <Tabs
                    variant={tabs.size > 0 ? "scrollable" : "fullWidth"}
                    value={value}
                    scrollButtons
                    onChange={handleChange}
                    textColor="inherit"
                    TabIndicatorProps={{
                        style: { display: "none" },
                    }}
                >
                    {Array.from(tabs).map(([mapKey, item], index) => (
                        <Tab
                            onContextMenu={(e) => handleRightClick(e, mapKey)}
                            className={
                                key === mapKey ? "tab-active" : "tab-unactive"
                            }
                            sx={{
                                fontWeight:
                                    key === mapKey ||
                                    pendingChangesOnTab(mapKey)
                                        ? "bold"
                                        : "",
                            }}
                            key={index}
                            label={
                                <Tooltip
                                    title={item.tooltip}
                                    placement="bottom"
                                >
                                    <div className="tab-header">
                                        <div className="return-node-icon-container">
                                            {getReturnNodeIcon(
                                                item?.scheduleType,
                                                tabsProperties[item?.key]
                                                    ?.isLocked,
                                            )}
                                        </div>

                                        <div
                                            className="tab-name"
                                            style={{
                                                color: determineTabTextColor(
                                                    product.taxYear,
                                                    item.taxYear,
                                                    company?.name ===
                                                        item?.companyName,
                                                ),
                                            }}
                                        >
                                            {(pendingChangesOnTab(mapKey)
                                                ? "* "
                                                : "") + item.tabName}
                                        </div>

                                        <div
                                            className="close-button"
                                            onClick={(e) =>
                                                handleDeleteClick(
                                                    e,
                                                    index,
                                                    item.component.key ?? "",
                                                )
                                            }
                                        >
                                            <Cancel fontSize="small" />
                                        </div>
                                    </div>
                                </Tooltip>
                            }
                        />
                    ))}
                </Tabs>
                {tabs && tabs.size > 0 && (
                    <Tooltip title={"Close all tabs"} placement="bottom">
                        <IconButton
                            aria-label="Close all tabs"
                            className="close-all-button"
                            onClick={() => handleCloseAllClick()}
                        >
                            <Cancel
                                fontSize="medium"
                                sx={{
                                    fill: "white",
                                }}
                            />
                        </IconButton>
                    </Tooltip>
                )}
            </AppBar>
            {Array.from(tabs.values()).map((item, index) => (
                <TabPanel key={index} value={value} index={index}>
                    {React.cloneElement(item.component, {
                        isActive: index === value,
                    })}
                </TabPanel>
            ))}
            <CustomSnackbar
                open={snackbarOpen}
                setOpen={setSnackbarOpen}
                message={snackbarMessage}
                severity={snackbarStatus}
            />
            <Menu
                open={contextMenu !== null}
                onClose={handleMenuClose}
                anchorReference="anchorPosition"
                anchorPosition={
                    contextMenu !== null
                        ? {
                              top: contextMenu.mouseY,
                              left: contextMenu.mouseX,
                          }
                        : undefined
                }
            >
                <MenuItem onClick={handleSplitScreen}>
                    Open in Split Screen
                </MenuItem>
                {splitScreen && (
                    <MenuItem
                        onClick={() => {
                            setSplitScreen(false);
                            handleMenuClose();
                        }}
                    >
                        Close Split Screen
                    </MenuItem>
                )}
            </Menu>
        </Box>
    );
};

export default MultiTab;
